import type { AttachmentFit, TowerKit } from "../kit/types";
import { ROOFED } from "./towerGroups";

/**
 * Thornwarden (Season 4, "The Siege of Thornwall": the War Campaign's Premium tier 30; docs/SHOP.md §15) — a briar
 * fortress: the keep has been taken by a living bramble that now wears it as armour (contract
 * tools/art/skins/README.md, "Big kits").
 *
 * The look: weathered fieldstone crept over by dark bramble veins, dark bark timber, bronze fittings and oxblood cloth.
 * Big kit: the stock roof is hidden and a CROWN OF THORNS takes its place — a twisted bramble wreath bristling with
 * great bone-tipped thorns round a woven bark cone wound with thorned vines, rose-hip buds glowing in the wreath; on the
 * archer, poison and beacon the crown stands on five arching thorned canes over the bow, the brew and the orb. A great
 * crimson rose with a glowing gold heart crowns the tip and blooms on the eaves: the roses breathe open and shut and
 * swell open as each shot nears (pulse + charge). Five thick thorned vines creep up the drum, a glow of sap travelling
 * up their seams (flow). The plinth sinks into a bramble root mound; great thorns break out of the side corners, a
 * rosebush grows at the front. The lightning tower keeps its roof (its coil stands in it) and wears the living spire on
 * its eave; frost, the arcane spire and the hex totem wear a thorned rose wreath. In a level: rose petals drift off the
 * crown with a gold glint, and the shots land in alternating bursts of petals and thorn splinters, the roses' hearts
 * flaring as they charge. Props: art/hq/skins/thornwarden/kit.glb (tools/art/skins/thornwarden.py).
 */
/** The roof storeys that hold the weapon or the element (bow, brew, the beacon's orb): the crown stands on canes there. */
const BOWERS = ["archer", "poison", "beacon"];
/** Every other roofed top wears the closed crown (lightning's roof is refused by kit/hide.ts: `ifHidden` skips it). */
const CROWNED = ROOFED.filter((t) => !BOWERS.includes(t));
/** The beacon's orb stands in its roof's upper half: its bower rises this far, metres (as Bronze's pavilion). */
const BEACON_RISE = 0.27;
/** The crown stands this proud of the stock roof's widest radius (unit envelope fraction). */
const CROWN_MARGIN = 0.08;
/** The roses: a slow breath, and the swell + heart flare as the shot nears. */
const ROSE = { pulse: 0.1, hz: 0.28, charge: { pulse: 0.35, glow: 2.2 } } as const;
const EAVE_ROSE = [1.8, 2.0, 2.2] as const;
/** The gallery towers' eave sockets sit inside the posts under a wide roof: eave props stand out past it. */
const WIDE = ["archer", "poison", "lightning"];
const wide = (fit: AttachmentFit): Readonly<Record<string, AttachmentFit>> => Object.fromEntries(WIDE.map((t) => [t, fit]));
const FACE_TURN = 45;

export const THORNWARDEN: TowerKit = {
  id: "tower.thornwarden",
  asset: "skins/thornwarden/kit",
  hide: ["roof"],
  // the hex keeps its feather crown (its mask stands in it); the arcane its collar (the wreath rings it)
  hideFit: { hex: [], arcane: [] },
  tier4: true,
  slots: {
    stone: { tint: 0x8c8274, mix: 0.85, gain: 0.85, roughness: 0.92, metalness: 0, keepAccents: false, pattern: { kind: "veins", colour: 0x2b1a10, scale: 5, strength: 0.8 } },
    roof: { tint: 0x5a2c18, mix: 0.92, gain: 0.95, roughness: 0.86, metalness: 0, keepAccents: false, trim: { colour: 0xd89a50, strength: 0.4 }, pattern: { kind: "grain", colour: 0x1c0e08, scale: 14, strength: 0.6 } },
    wood: { tint: 0x3d2b1d, mix: 0.88, gain: 0.82, roughness: 0.9, keepAccents: false, pattern: { kind: "grain", colour: 0x170e07, scale: 11, strength: 0.65 } },
    metal: { tint: 0x9c6a34, mix: 0.9, gain: 1.0, metalness: 0.95, roughness: 0.36, keepAccents: false, trim: { colour: 0xe8b068, strength: 0.45 }, pattern: { kind: "hammered", colour: 0x5a3416, scale: 22, strength: 0.35 } },
    cloth: { tint: 0x7a1424, mix: 0.95, gain: 1.05, roughness: 0.7, keepAccents: false, trim: { colour: 0xe0a860, strength: 0.6 } },
  },
  attachments: [
    // big kit: the crown of thorns in the hidden roof's place; its buds flare with each shot
    { piece: "briarcrown", at: [], towers: CROWNED, ifHidden: "roof", fitTo: { to: "roof", margin: CROWN_MARGIN }, motion: { charge: { glow: 3 } }, fit: {
      barracks: { fitTo: { to: "roof", shape: "box", margin: 0.14 } },
    } },
    { piece: "briarbower", at: [], towers: BOWERS, ifHidden: "roof", fitTo: { to: "roof", margin: CROWN_MARGIN }, motion: { charge: { glow: 3 } }, fit: { beacon: { offset: [0, BEACON_RISE, 0] } } },
    // the rose on the crown's tip
    { piece: "bloom", at: ["apex"], towers: [...CROWNED.filter((t) => t !== "lightning"), ...BOWERS], ifHidden: "roof", scale: [2.2, 2.4, 2.6], offset: [0, -0.03, 0], motion: ROSE, fit: { beacon: { offset: [0, BEACON_RISE - 0.03, 0] } } },
    // roses blooming on the three eaves in view (crest1, the back, is also the mortar's barrel side; the slender shafts
    // on their band, the hex totem two of them: its wreath and spire already carry roses)
    { piece: "bloom", at: ["crest0", "crest2", "crest3"], scale: EAVE_ROSE, offset: [0, 0.02, 0.03], motion: ROSE, fit: {
      ...wide({ offset: [0, 0.02, 0.2] }),
      arcane: { at: ["band0", "band2", "band3"], scale: [1.1, 1.2, 1.3], offset: [0, 0, 0.03] }, hex: { at: ["band0", "band2"], scale: [1.1, 1.2, 1.3], offset: [0, 0, 0.03] },
    } },
    // the living spire where the roof stays: lightning's front-left eave (crest0), the hex totem's headdress
    { piece: "spire", at: ["apex"], towers: ["lightning", "hex"], ifShown: "roof", scale: [1.6, 1.75, 1.9], fit: {
      lightning: { at: ["crest0"], yaw: FACE_TURN, offset: [0, 0.04, 0.16], scale: [1.25, 1.35, 1.45] },
      hex: { offset: [0, 0.04, -0.06], scale: [1.15, 1.25, 1.35] },
    } },
    // the thorned rose wreath: round frost's battlement under its crystal, the arcane collar, the hex pole under its mask
    { piece: "wreath", at: ["top"], towers: ["frost", "arcane", "hex"], scale: [0.7, 0.74, 0.8], offset: [0, -0.12, 0], fit: {
      arcane: { at: ["apex"], scale: [0.48, 0.5, 0.52], offset: [0, -0.02, 0] }, hex: { scale: [0.44, 0.47, 0.5], offset: [0, -0.06, 0] },
    } },
    // vines creeping up the drum, a glow of sap travelling up their seams
    { piece: "thornvines", at: [], fitTo: { to: "drum", taper: true, margin: 0.04 }, motion: { flow: { speed: 0.22, length: 0.45, along: "up", glow: 2.6, bulge: 0.006 } }, fit: {
      barracks: { fitTo: { to: "drum", shape: "box", margin: 0.1 } },
    } },
    // the bramble root mound the plinth sinks into
    { piece: "rootmound", at: [], fitTo: { to: "plinth", height: "keep" } },
    { piece: "thorns", at: ["corner0", "corner2"], scale: [1.5, 1.65, 1.8] },
    { piece: "rosebush", at: ["corner3"], scale: [1.5, 1.65, 1.8], motion: { sway: 2.5, hz: 0.3 } },
    { piece: "leaf", at: ["corner3"], offset: [0.06, 0.4, 0.06], scale: 1.2, detail: true, motion: { twinkle: 0.24, spin: 1.0, hz: 0.3 } },
  ],
  idle: {
    kind: "leaves", colour: 0xb8203c, every: [1.6, 2.6], at: "apex", lift: 0.1, count: 4, radius: 0.24,
    also: { kind: "glints", colour: 0xf0c070, count: 2, radius: 0.16 },
    fit: { frost: { at: "top", lift: 0.1 }, lightning: { at: "crest0", lift: 0.3 }, arcane: { at: "band0", lift: 0.1 } },
  },
  // petals and thorn splinters in turn; the rose hearts' gold drawn in as the shot winds up
  flourish: {
    impacts: [{ kind: "leafburst", colour: 0xc02040, count: 8 }, { kind: "sparks", colour: 0xf0dcb0, count: 7 }],
    muzzle: { colour: 0xffc878, count: 3 },
    charge: { colour: 0xe8486a, count: 4 },
  },
  preview: { tower: "archer", tier: 3 },
};
